import React, { useCallback, useEffect, useState } from "react";
import api, { authFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import SiteAddressPicker from "@/components/SiteAddressPicker";
import { Clock, FileSpreadsheet, Loader2, MapPin, RefreshCw, Satellite, Settings2, AlertTriangle } from "lucide-react";

/**
 * La giornata dei mezzi: quando hanno lasciato la sede, quando hanno
 * agganciato il cantiere, quando sono rientrati, e quante ore ci sono stati.
 *
 * I dati vengono dal satellitare, registrato dal server ogni pochi minuti. Non
 * e' un cronometro: e' il racconto di dove e' stato un mezzo, con la precisione
 * del campionamento. Lo vede solo l'amministratore, perche' dice anche dove e'
 * stata una persona, ora per ora.
 */
const ora = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};
const durata = (minuti) => `${Math.floor((minuti || 0) / 60)}:${String(Math.round(minuti || 0) % 60).padStart(2, "0")}`;
const oggi = () => new Date().toISOString().slice(0, 10);

export default function GiornataMezzi() {
  const [giorno, setGiorno] = useState(oggi);
  const [dati, setDati] = useState(null);
  const [carico, setCarico] = useState(true);
  const [imp, setImp] = useState(null);
  const [apriImp, setApriImp] = useState(false);
  const [aperto, setAperto] = useState(null);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const { data } = await api.get(`/tracce/giornata?giorno=${giorno}`);
      setDati(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel report"); }
    finally { setCarico(false); }
  }, [giorno]);

  useEffect(() => { carica(); }, [carica]);
  useEffect(() => {
    api.get("/tracce/impostazioni").then(({ data }) => setImp(data)).catch(() => {});
  }, []);

  const scarica = async () => {
    try {
      const r = await authFetch(`/tracce/giornata/excel?giorno=${giorno}`);
      if (!r.ok) throw new Error();
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = url; a.download = `Giornata mezzi ${giorno}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore nel download"); }
  };

  const senzaSede = imp && (imp.sede_lat === undefined || imp.sede_lat === null);

  return (
    <div className="space-y-4" data-testid="giornata-mezzi">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Solo amministratore</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Giornata dei mezzi</h1>
          <p className="text-sm text-slate-500 mt-1">
            Dal satellitare: uscita dalla sede, arrivo e partenza dal cantiere, rientro, ore in cantiere.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Input type="date" value={giorno} max={oggi()} onChange={(e) => setGiorno(e.target.value)}
                 className="h-10 w-40" data-testid="giorno-report" />
          <Button variant="outline" className="h-10" onClick={carica} disabled={carico}>
            <RefreshCw className={`w-4 h-4 mr-2 ${carico ? "animate-spin" : ""}`} /> Aggiorna
          </Button>
          <Button variant="outline" className="h-10" onClick={scarica} data-testid="excel-giornata">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button variant="outline" className="h-10" onClick={() => setApriImp((x) => !x)}
                  data-testid="apri-impostazioni">
            <Settings2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {senzaSede && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold">Manca la posizione della sede</div>
              Senza, il report non sa dire quando un mezzo è uscito o rientrato.
              Aprila con l&apos;ingranaggio qui sopra e scegli l&apos;indirizzo dai suggerimenti.
            </div>
          </CardContent>
        </Card>
      )}

      {apriImp && imp && <Impostazioni imp={imp} onSalvato={(x) => { setImp(x); carica(); }} />}

      {dati && (
        <div className="flex flex-wrap gap-2 text-xs text-slate-500 items-center">
          <Badge variant="outline" className="text-[11px]">
            <Satellite className="w-3 h-3 mr-1" /> una lettura ogni {dati.minuti_giro} minuti
          </Badge>
          <Badge variant="outline" className="text-[11px]">raggio {dati.raggio_m} m</Badge>
          <span>{dati.totali.mezzi} mezzi · {dati.totali.con_cantiere} in cantiere ·
            {" "}{durata(dati.totali.minuti_cantiere)} di ore in cantiere in tutto</span>
          {dati.cantieri_senza_coordinate > 0 && (
            <span className="text-amber-700">
              · {dati.cantieri_senza_coordinate} cantieri senza indirizzo sulla mappa
              {dati.quali_senza_coordinate?.length > 0 && `: ${dati.quali_senza_coordinate.slice(0, 3).join(", ")}`}
            </span>
          )}
        </div>
      )}

      {carico && !dati && (
        <p className="text-sm text-slate-500 py-10 text-center flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Ricostruisco la giornata...
        </p>
      )}

      {dati && dati.mezzi.length === 0 && (
        <Card className="border-dashed border-2"><CardContent className="p-8 text-center text-slate-500">
          Nessuna posizione registrata in questa giornata. Le registrazioni partono dal
          momento in cui il server è stato aggiornato: per i giorni prima non c&apos;è storia.
        </CardContent></Card>
      )}

      {dati && dati.mezzi.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Mezzo</th>
                  <th className="text-left px-3 py-2">Autista</th>
                  <th className="text-center px-3 py-2">Lascia la sede</th>
                  <th className="text-center px-3 py-2">Arriva in cantiere</th>
                  <th className="text-center px-3 py-2">Lascia il cantiere</th>
                  <th className="text-center px-3 py-2">Rientra in sede</th>
                  <th className="text-right px-3 py-2">Ore in cantiere</th>
                  <th className="text-left px-3 py-2">Cantiere</th>
                </tr>
              </thead>
              <tbody>
                {dati.mezzi.map((m) => (
                  <React.Fragment key={m.equipment_id}>
                    <tr className="border-t border-slate-100 hover:bg-orange-50/40 cursor-pointer"
                        onClick={() => setAperto(aperto === m.equipment_id ? null : m.equipment_id)}
                        data-testid={`riga-${m.identifier}`}>
                      <td className="px-3 py-1.5 font-semibold whitespace-nowrap">{m.identifier}</td>
                      <td className="px-3 py-1.5 text-slate-600 truncate max-w-[160px]">{m.autista || "—"}</td>
                      <td className="px-3 py-1.5 text-center tabular-nums">{ora(m.uscita_sede)}</td>
                      <td className="px-3 py-1.5 text-center tabular-nums">{ora(m.arrivo_cantiere)}</td>
                      <td className="px-3 py-1.5 text-center tabular-nums">{ora(m.partenza_cantiere)}</td>
                      <td className="px-3 py-1.5 text-center tabular-nums">{ora(m.rientro_sede)}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${
                        m.minuti_cantiere ? "" : "text-slate-300"}`}>{durata(m.minuti_cantiere)}</td>
                      <td className="px-3 py-1.5 text-slate-600 truncate max-w-[220px]">
                        {m.cantieri.map((c) => c.nome).join(" · ") || (
                          <span className="text-amber-700">{m.mancanze.join("; ")}</span>
                        )}
                      </td>
                    </tr>
                    {aperto === m.equipment_id && (
                      <tr className="bg-slate-50/60">
                        <td colSpan={8} className="px-3 py-2">
                          <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">
                            La giornata, sosta per sosta
                          </div>
                          <div className="space-y-1">
                            {m.tappe.map((t, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs">
                                <span className="tabular-nums w-24 flex-shrink-0">
                                  {ora(t.inizio)}–{ora(t.fine)}
                                </span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide flex-shrink-0 ${
                                  t.genere === "sede" ? "bg-slate-200 text-slate-700"
                                    : t.genere === "cantiere" ? "bg-emerald-100 text-emerald-700"
                                      : "bg-sky-100 text-sky-700"}`}>
                                  {t.genere}
                                </span>
                                <span className="min-w-0 truncate">
                                  {t.nome}
                                  {t.genere === "viaggio" && (t.town || t.street)
                                    && <span className="text-slate-400"> · ultimo punto {t.street || ""} {t.town || ""}</span>}
                                </span>
                                <span className="ml-auto text-slate-500 tabular-nums flex-shrink-0">
                                  <Clock className="w-3 h-3 inline-block mr-1" />{durata(t.minuti)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-slate-400">
        Gli orari hanno la precisione della lettura: il satellitare viene interrogato ogni
        pochi minuti, quindi un orario può discostarsi di altrettanto. Un cantiere si aggancia
        quando il mezzo entra nel raggio impostato, e si sgancia quando ne esce.
      </p>
    </div>
  );
}

function Impostazioni({ imp, onSalvato }) {
  const [f, setF] = useState({
    sede_indirizzo: imp.sede_indirizzo || "", sede_lat: imp.sede_lat ?? null,
    sede_lon: imp.sede_lon ?? null, raggio_m: imp.raggio_m || 1000,
    attivo: imp.attivo !== false,
  });
  const [salvo, setSalvo] = useState(false);

  const salva = async () => {
    setSalvo(true);
    try {
      const { data } = await api.put("/tracce/impostazioni", {
        ...f, raggio_m: parseInt(f.raggio_m) || 1000,
      });
      toast.success("Impostazioni salvate");
      onSalvato(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSalvo(false); }
  };

  const provaAdesso = async () => {
    try {
      const { data } = await api.post("/tracce/giro");
      toast.success(`Satellitare letto: ${data.letti} mezzi, ${data.scritti} posizioni nuove`);
    } catch (e) { toast.error(e.response?.data?.detail || "Il satellitare non risponde"); }
  };

  return (
    <Card className="border-slate-200">
      <CardContent className="p-4 space-y-3">
        <h2 className="font-display font-bold flex items-center gap-2">
          <MapPin className="w-4 h-4 text-orange-500" /> Da dove partono i mezzi
        </h2>
        {/* Lo stesso campo dei cantieri: scegliendo dai suggerimenti arrivano
            anche le coordinate, che sono quelle che contano qui. */}
        <SiteAddressPicker value={{ address: f.sede_indirizzo, comune: null }}
                           onChange={(patch) => setF((p) => ({
                             ...p,
                             sede_indirizzo: patch.address ?? p.sede_indirizzo,
                             sede_lat: patch.lat ?? p.sede_lat,
                             sede_lon: patch.lng ?? p.sede_lon,
                           }))} />
        <div className="text-xs text-slate-500">
          {f.sede_lat != null
            ? `Posizione registrata: ${Number(f.sede_lat).toFixed(5)}, ${Number(f.sede_lon).toFixed(5)}`
            : "Scegli l'indirizzo dai suggerimenti: serve la posizione sulla mappa, non il testo."}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Raggio di aggancio (metri)</Label>
            <Input type="number" min="100" max="20000" step="50" value={f.raggio_m}
                   onChange={(e) => setF({ ...f, raggio_m: e.target.value })}
                   data-testid="raggio-metri" />
            <p className="text-xs text-slate-500 mt-1">
              Dentro questa distanza il mezzo è considerato «in cantiere». Mille metri
              tengono dentro un cantiere lungo senza agganciare chi passa in tangenziale.
            </p>
          </div>
          <div>
            <Label>Registrazione</Label>
            <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer">
              <input type="checkbox" checked={f.attivo} className="w-4 h-4 accent-orange-500"
                     onChange={(e) => setF({ ...f, attivo: e.target.checked })} />
              Tieni traccia delle posizioni
            </label>
            <p className="text-xs text-slate-500 mt-1">
              {imp.punti_in_archivio?.toLocaleString("it-IT") || 0} posizioni in archivio,
              conservate sei mesi. Una lettura ogni {imp.minuti_giro} minuti.
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={salva} disabled={salvo}
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            {salvo ? "Salvo..." : "Salva"}
          </Button>
          <Button variant="outline" onClick={provaAdesso} data-testid="prova-satellitare">
            <Satellite className="w-4 h-4 mr-2" /> Prova il satellitare adesso
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
